import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Receipt, ArrowRight, CheckCircle2, TrendingDown, ClipboardList, Search,
  Landmark, BarChart3, Newspaper, Megaphone, Pin, MapPin, Vote, FileText, Clock,
} from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { Skeleton } from './ui/skeleton';
import { api } from '../lib/api';
import { SectionHeader, formatDate } from './Shared';

// Shared hook: campaign content (tax promise + section toggles + thank-you text)
export const useCampaignContent = () => {
  const [content, setContent] = useState(null);
  useEffect(() => {
    api.get('/campaign/content').then((r) => setContent(r.data)).catch(() => {});
  }, []);
  return content;
};

/* ------------------------------------------------------------------ */
/* THE TAX PROMISE                                                     */
/* ------------------------------------------------------------------ */
export const TaxPromiseSection = ({ promise, compact = false }) => {
  if (!promise) return null;
  if (compact) {
    return (
      <Card className="border-[#C9A84C] bg-[#FFFDF6]" data-testid="tax-promise-compact">
        <CardContent className="p-5">
          <p className="text-xs font-bold uppercase tracking-widest text-[#8a6f22] inline-flex items-center gap-1.5"><TrendingDown className="h-4 w-4" /> Steve's Tax Promise</p>
          <p className="font-serif font-black text-[#1a2744] text-lg mt-2 leading-snug break-words">{promise.headline}</p>
          <p className="text-sm text-slate-700 mt-2 leading-relaxed">{promise.subtitle}</p>
          <p className="text-xs text-slate-500 mt-2 leading-relaxed">{promise.disclaimer}</p>
          <Button asChild size="sm" className="mt-3 h-10 font-bold bg-[#1a2744] hover:bg-[#243456] text-white btn-press" data-testid="tax-promise-read-btn">
            <Link to="/platform#tax-promise">Read the Tax Promise <ArrowRight className="h-3.5 w-3.5 ml-1" /></Link>
          </Button>
        </CardContent>
      </Card>
    );
  }
  return (
    <section id="tax-promise" className="bg-[#FFFDF6] border-y border-[#C9A84C]/40 py-12 md:py-16" data-testid="tax-promise-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader kicker="THE TAX PROMISE" title={promise.headline} dek={promise.subtitle} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-6 items-start">
          <div>
            <p className="text-slate-700 leading-relaxed" data-testid="tax-promise-expanded">{promise.expanded}</p>
            <p className="text-slate-700 leading-relaxed mt-3 font-semibold">{promise.services_note}</p>
            <p className="text-sm text-slate-700 mt-4 leading-relaxed bg-white border border-slate-200 rounded-xl p-4">{promise.target_wording}</p>
            <p className="text-xs text-slate-500 mt-3 leading-relaxed" data-testid="tax-promise-disclaimer">{promise.disclaimer}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6">
            <p className="font-serif font-bold text-[#1a2744]">How Steve will push for it</p>
            <ul className="mt-3 space-y-2.5">
              {(promise.bullets || []).map((b, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700 leading-relaxed">
                  <CheckCircle2 className="h-4 w-4 text-[#8a6f22] shrink-0 mt-0.5" /> {b}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/* REPORT IT. TRACK IT. CUT THE WASTE. LOWER THE TAXES.                */
/* ------------------------------------------------------------------ */
export const ReportTrackBlock = ({ promise }) => {
  if (!promise) return null;
  return (
    <section className="bg-[#1a2744] py-12 md:py-16" data-testid="report-track-block">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="font-serif font-black text-white text-2xl sm:text-3xl lg:text-4xl leading-tight break-words" data-testid="report-track-title">{promise.combined_title}</h2>
        <p className="text-slate-300 mt-4 leading-relaxed max-w-3xl mx-auto">{promise.combined_body}</p>
        <p className="text-[#e8d9ae] font-bold mt-4">{promise.combined_callout}</p>
        <div className="flex flex-wrap justify-center gap-3 mt-7">
          <Button asChild className="h-12 px-5 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press" data-testid="rt-report-btn">
            <Link to="/issue-tracker/report">Report a Ward 8 Issue</Link>
          </Button>
          <Button asChild variant="outline" className="h-12 px-5 border-white bg-transparent text-white hover:bg-white/10 font-bold btn-press" data-testid="rt-hub-btn">
            <Link to="/issue-tracker">View the Issue Tracker</Link>
          </Button>
          <Button asChild variant="outline" className="h-12 px-5 border-white bg-transparent text-white hover:bg-white/10 font-bold btn-press" data-testid="rt-data-btn">
            <Link to="/city-data">View City Data</Link>
          </Button>
          <Button asChild variant="outline" className="h-12 px-5 border-white bg-transparent text-white hover:bg-white/10 font-bold btn-press" data-testid="rt-ward-btn">
            <Link to="/whos-running#find-your-ward">Find Your Ward</Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/* STEVE IS WORKING FOR WARD 8 — proof cards + latest action feed      */
/* ------------------------------------------------------------------ */
const PROOF_CARDS = [
  { key: 'issues_reported', label: 'Issues Reported', icon: ClipboardList },
  { key: 'issues_resolved', label: 'Issues Resolved', icon: CheckCircle2 },
  { key: 'avg_days_open', label: 'Average Days Open', icon: Clock },
  { key: 'charts_published', label: 'City Data Charts Published', icon: BarChart3 },
  { key: 'meetings_tracked', label: 'Council Meetings Tracked', icon: Landmark },
  { key: 'explainers_published', label: 'Newsroom Explainers Published', icon: Newspaper },
  { key: 'updates_posted', label: 'Public Updates Posted', icon: Pin },
];

const ACTION_TYPE_LABELS = {
  action_hub: 'Issue Tracker', city_data: 'City Data', meetings: 'Council', newsroom: 'Newsroom',
  election: 'Election', ward8: 'Ward 8', question: 'Question to City Hall', followup: 'Follow-up', notice: 'Public Notice',
};

export const WorkingForWard8 = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/campaign/working-stats').then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return null;
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16" data-testid="working-for-ward8-section">
      <SectionHeader kicker="PROOF, NOT PROMISES" title="Proof Steve Is Working for Ward 8" dek={data.subtitle} />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
        {PROOF_CARDS.map(({ key, label, icon: Icon }) => (
          <Card key={key} className="text-center" data-testid={`proof-card-${key}`}>
            <CardContent className="p-4">
              <Icon className="h-5 w-5 text-[#8a6f22] mx-auto" />
              <p className="text-2xl sm:text-3xl font-black text-[#1a2744] mt-2 leading-none">{data.counts?.[key] ?? 0}</p>
              <p className="text-[11px] sm:text-xs font-semibold text-slate-500 mt-1.5 leading-tight">{label}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="mt-6">
        <Button asChild className="h-12 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="proof-view-tracker-button">
          <Link to="/issue-tracker">View the Ward 8 Issue Tracker App (PITA) <ArrowRight className="h-4 w-4 ml-1" /></Link>
        </Button>
      </div>
      {(data.latest_actions || []).length > 0 && (
        <div className="mt-10" data-testid="latest-action-feed">
          <p className="font-serif font-bold text-[#1a2744] text-lg sm:text-xl">Latest Action from Steve</p>
          <div className="mt-4 space-y-3">
            {data.latest_actions.map((a) => (
              <Card key={a.id} data-testid="latest-action-item">
                <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline" className="bg-[#EAF2FF] text-[#1a2744] border-[#CFE0FF] text-[10px] font-bold">{ACTION_TYPE_LABELS[a.type] || a.type || 'Update'}</Badge>
                      {a.badge && <Badge variant="outline" className="bg-[#FFF7E6] text-[#7A4E00] border-[#F2D08A] text-[10px] font-bold">{a.badge}</Badge>}
                      <span className="text-[11px] text-slate-400">{formatDate(a.date)}</span>
                    </div>
                    <p className="font-bold text-[#1a2744] mt-1.5 break-words">{a.title}</p>
                    {a.summary && <p className="text-sm text-slate-600 mt-0.5 leading-relaxed break-words">{a.summary}</p>}
                  </div>
                  {a.url && (
                    <Button asChild variant="outline" size="sm" className="font-bold shrink-0 self-start sm:self-center">
                      <Link to={a.url}>{a.button_label || 'View'} <ArrowRight className="h-3.5 w-3.5 ml-1" /></Link>
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

/* ------------------------------------------------------------------ */
/* WHAT'S NEW — aggregated updates with filters                        */
/* ------------------------------------------------------------------ */
const WHATS_NEW_TABS = [
  { value: 'all', label: 'All' },
  { value: 'action_hub', label: 'Issue Tracker' },
  { value: 'city_data', label: 'City Data' },
  { value: 'meetings', label: 'Council Meetings' },
  { value: 'newsroom', label: 'Newsroom' },
  { value: 'election', label: 'Election Centre' },
  { value: 'ward8', label: 'Ward 8 Updates' },
];

const TYPE_ICONS = { action_hub: MapPin, city_data: BarChart3, meetings: Landmark, newsroom: Newspaper, election: Vote, ward8: Megaphone };

export const WhatsNewSection = () => {
  const [tab, setTab] = useState('all');
  const [items, setItems] = useState(null);
  useEffect(() => {
    setItems(null);
    api.get(`/whats-new?category=${tab}`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [tab]);
  return (
    <section className="bg-white border-y border-slate-200 py-12 md:py-16" data-testid="whats-new-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader kicker="STAY INFORMED" title="What's New" dek="Latest updates from the Issue Tracker, City Data, Council Meetings, Newsroom, and Election Centre." />
        <Tabs value={tab} onValueChange={setTab} className="mt-5">
          <TabsList className="flex flex-wrap h-auto gap-1 justify-start bg-slate-100 p-1" data-testid="whats-new-tabs">
            {WHATS_NEW_TABS.map((t) => (
              <TabsTrigger key={t.value} value={t.value} className="text-xs sm:text-sm font-bold data-[state=active]:bg-[#1a2744] data-[state=active]:text-white" data-testid={`whats-new-tab-${t.value}`}>{t.label}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        {items === null ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-44 rounded-2xl" />)}
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-500 mt-6" data-testid="whats-new-empty">No updates in this category yet. Check back soon.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6" data-testid="whats-new-grid">
            {items.slice(0, 9).map((it) => {
              const Icon = TYPE_ICONS[it.type] || Megaphone;
              return (
                <Card key={it.id} className={`flex flex-col ${it.pinned ? 'border-[#C9A84C]' : ''}`} data-testid="whats-new-card">
                  <CardContent className="p-5 flex flex-col flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline" className="bg-[#EAF2FF] text-[#1a2744] border-[#CFE0FF] text-[10px] font-bold inline-flex items-center gap-1"><Icon className="h-3 w-3" /> {WHATS_NEW_TABS.find((t) => t.value === it.type)?.label || it.type}</Badge>
                      {it.badge && <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 text-[10px] font-bold">{it.badge}</Badge>}
                      {it.pinned && <Pin className="h-3.5 w-3.5 text-[#C9A84C]" />}
                    </div>
                    <p className="font-serif font-bold text-[#1a2744] mt-2.5 leading-snug break-words">{it.title}</p>
                    <p className="text-[11px] text-slate-400 mt-1">{formatDate(it.date)}</p>
                    {it.summary && <p className="text-sm text-slate-600 mt-2 leading-relaxed break-words line-clamp-3 flex-1">{it.summary}</p>}
                    <Button asChild variant="outline" size="sm" className="font-bold mt-4 self-start">
                      <Link to={it.url || '/'}>{it.button_label || 'Read More'} <ArrowRight className="h-3.5 w-3.5 ml-1" /></Link>
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/* CITY DATA intro block                                               */
/* ------------------------------------------------------------------ */
export const CityDataTaxIntro = () => (
  <div className="bg-[#FFFDF6] border border-[#C9A84C]/50 rounded-2xl p-5 sm:p-6" data-testid="citydata-tax-intro">
    <p className="text-xs font-bold uppercase tracking-widest text-[#8a6f22] inline-flex items-center gap-1.5"><Receipt className="h-4 w-4" /> Why this matters</p>
    <h2 className="font-serif font-black text-[#1a2744] text-xl sm:text-2xl mt-2">City Data Should Help Lower Taxes</h2>
    <p className="text-sm sm:text-base text-slate-700 mt-2 leading-relaxed">Residents deserve to see where money is going, what services cost, where systems are failing, and where better choices could save taxpayers money. Charts connect to Newsroom explainers through the More Info button.</p>
    <div className="flex flex-wrap gap-2.5 mt-4">
      <Button asChild size="sm" className="h-10 font-bold bg-[#1a2744] hover:bg-[#243456] text-white btn-press" data-testid="citydata-taxpromise-btn"><Link to="/platform#tax-promise">View Tax Promise</Link></Button>
      <Button asChild size="sm" variant="outline" className="h-10 font-bold border-[#1a2744] text-[#1a2744] btn-press" data-testid="citydata-report-btn"><Link to="/issue-tracker/report">Report a Service Issue</Link></Button>
      <Button asChild size="sm" variant="outline" className="h-10 font-bold border-[#1a2744] text-[#1a2744] btn-press" data-testid="citydata-explainers-btn"><Link to="/newsroom">Read Newsroom Explainers</Link></Button>
    </div>
  </div>
);
